/** Filter controls for the list screens.
 *
 * Every filter here narrows the query sent to the SERVER, not the rows already on screen.
 * That distinction is the whole point: a filter over fetched rows answers "is it on this page",
 * and the question an access-control investigation actually asks - "did this ever happen" -
 * needs the whole log. A door filter that only searched the visible 200 rows would be worse
 * than no filter, because it would answer "no" convincingly.
 */
import type { ReactNode } from "react";

export function FilterBar({
  children,
  onClear,
  activeCount,
}: {
  children: ReactNode;
  onClear: () => void;
  /** How many filters are set, so it is obvious WHY a list looks short. */
  activeCount: number;
}) {
  return (
    <div className="filters" role="search">
      {children}
      <button type="button" className="btn" onClick={onClear} disabled={activeCount === 0}>
        Clear {activeCount > 0 ? `(${activeCount})` : ""}
      </button>
    </div>
  );
}

export function FilterSelect<T extends string>({
  label,
  value,
  options,
  onChange,
  anyLabel = "Any",
}: {
  label: string;
  value: T | "";
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (value: T | "") => void;
  anyLabel?: string;
}) {
  return (
    <label className="filter">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value as T | "")}>
        <option value="">{anyLabel}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function FilterText({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="filter">
      <span>{label}</span>
      <input
        type="search"
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

/** A date, sent to the API as an ISO instant.
 *
 * `to` is EXCLUSIVE on the server, and a person typing "16 September" in a "to" box means "up
 * to the end of the 16th". So an end date is pushed to the start of the following day here,
 * rather than silently omitting the last day of every range the user asks for.
 */
export function FilterDate({
  label,
  value,
  onChange,
  endOfDay = false,
}: {
  label: string;
  /** yyyy-mm-dd, as the native date input uses. */
  value: string;
  onChange: (value: string) => void;
  endOfDay?: boolean;
}) {
  return (
    <label className="filter">
      <span>{label}</span>
      <input type="date" value={value} onChange={(event) => onChange(event.target.value)} />
      {endOfDay ? <span className="visually-hidden">inclusive of the whole day</span> : null}
    </label>
  );
}

/** yyyy-mm-dd -> an ISO instant the API accepts. `endOfDay` adds a day, because the API's `to`
 * bound is exclusive so that adjacent ranges cannot double-count a row on the boundary. */
export function dateParam(value: string, endOfDay = false): string | undefined {
  if (!value) return undefined;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return undefined;
  if (endOfDay) date.setDate(date.getDate() + 1);
  return date.toISOString();
}
